<template>
  <div class="modal" @click.self="close">
    <button @click="closeModal()" class="modal__Overlay"></button>
    <success-prompt :updated="updated" :message="prompt.success"></success-prompt>

    <div class="modal__Inner">
      <section class="modal__Post">
        <error-prompt class="error--Modal" :errors="errors"></error-prompt>
        <loader class="imageLoader" :loading="loading || updating" />

        <header class="modal__Header">
          <h1 class="modal__Title">Edit organisation</h1>

          <div class="modal__Meta">
            <ul>
              <li class="modal__MetaItem--Button">
                <button @click="deleteOrg()" :disabled="updated === true" class="button--Small button--Brand">
                  Delete organisation
                </button>
              </li>
              <li class="modal__MetaItem--Button">
                <button @click="update('org')" :disabled="updated === true" class="button--Small">
                  {{ prompt.save }}
                </button>
              </li>
              <li class="modal__MetaItem">
                <button @click="closeModal()" class="modal__MetaLink">
                  <icon name="times"></icon>
                </button>
              </li>
            </ul>
          </div>
        </header>

        <template v-if="item">
          <div class="cardPreview">
            <div class="cardPreview__Columns">
              <div class="cardPreview__Column" v-if="item.status !== undefined">
                <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('status') }}</h2>
                <p class="cardPreview__Status">{{ capitalize(item.status) }}</p>
              </div>
              <div class="cardPreview__Column" v-if="$route.params.id !== undefined">
                <h2 class="cardPreview__Title">{{ $store.getters.getStringTranslation('controls') }}</h2>
                <ul class="cardPreview__Meta">
                  <li>
                    <button class="cardPreview__MetaButton" @click="disable('org')">
                      <icon v-if="item.status === 'active'" name="eye-slash"></icon>
                      <icon v-else name="eye"></icon>
                      {{ disableLabel }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="formSection formSection--Editor">
            <ul class="formSection__Types" v-if="tabs.length > 0">
              <li class="formSection__TypesTab" v-for="tab in tabs" :key="tab.id">
                <input type="radio" name="type" v-model="currentTab" :id="`type-${tab.value}`" :value="tab.value" />
                <label :for="`type-${tab.value}`">
                  <icon :name="tab.icon"></icon>
                  {{ tab.label }}
                </label>
              </li>
            </ul>

            <org-fields :org="item" v-model="item" :loading="loading" v-if="currentTab === 'general'" />

            <websites
              :filters="{}"
              :org="item"
              :allowSubscription="true"
              v-model="item.api_keys"
              v-else-if="currentTab === 'websites'"
            />
            <active-users :orgID="$route.params.id" search v-else-if="currentTab === 'users'" />
            <pending-users :orgID="$route.params.id" search v-else-if="currentTab === 'pending'" />
          </div>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import HTTP from '@/http-common.js'

import GetProperty from '@/components/mixins/GetProperty.js'
import DisableItem from '@/components/mixins/DisableItem.js'
import Capitalize from '@/components/mixins/Capitalize.js'
import UpdatePost from '@/components/mixins/UpdatePost.js'

import OrgFields from '@/components/editor/OrgFields.vue'
import Websites from '@/components/settings/Websites.vue'
import ActiveUsers from '@/components/users/ActiveUsers.vue'
import PendingUsers from '@/components/users/PendingUsers.vue'
import Loader from '@/components/partials/Loader.vue'
import ErrorPrompt from '@/components/editor/ErrorPrompt.vue'
import SuccessPrompt from '@/components/editor/SuccessPrompt.vue'

export default {
  name: 'EditOrganisation',
  components: {
    OrgFields,
    Websites,
    ActiveUsers,
    PendingUsers,
    Loader,
    ErrorPrompt,
    SuccessPrompt,
  },
  props: ['queryParams', 'orgs'],
  mixins: [GetProperty, UpdatePost, DisableItem, Capitalize],
  data() {
    return {
      item: null,
      tabs: [
        { value: 'general', icon: 'cog', label: 'General' },
        { value: 'websites', icon: 'globe', label: 'Websites' },
        { value: 'users', icon: 'users', label: 'Users' },
        { value: 'pending', icon: 'user-plus', label: 'Pending users' },
      ],
      currentTab: 'general',
      status: 'active',
      prompt: {
        save: 'Save',
        success: '',
      },
      parent: null,
      loading: true,
      updating: false,
      updated: false,
      errors: '',
    }
  },
  computed: {
    lang() {
      return this.$store.state.ui.lang
    },
  },
  created() {
    if (this.$route.params.id !== undefined) {
      HTTP.get(`org/${this.$route.params.id}`)
        .then((response) => {
          this.item = response.data
          this.status = response.data.status
          this.loading = false

          if (this.item.l10n[0] === undefined)
            this.item.l10n.push({ lang: 'en', description: '', foreword: '', contents: [] })

          this.prompt.save = 'Save'
          this.prompt.success = 'Success! Organisation updated.'
        })
        .catch((e) => {
          this.errors = e.response ? e.response.data : console.error(e)
          this.loading = false
        })
    } else {
      this.loading = false

      this.prompt.save = 'Create'
      this.prompt.success = 'Success! Organisation created.'
    }

    // Define parent URL
    this.parent = {
      path: this.$route.matched.find((x) => x.parent !== undefined).parent.path,
      query: {
        search: JSON.stringify(this.queryParams),
      },
    }
  },
  methods: {
    closeModal() {
      if (this.changed === true) {
        if (confirm('Are you sure you wish to close the editor? Everything not saved will be lost!')) {
          this.$router.push(this.parent)
        }
      } else {
        this.$router.push(this.parent)
      }
    },
    deleteOrg() {
      if (
        confirm(
          `Are you sure you wish to delete ${this.item.full_name}? All users and data will be permanently removed along with this organisation.`
        )
      ) {
        this.loading = true

        HTTP.delete(`org/${this.item.id}`).then(() => {
          this.$store.dispatch('deleteOrg', this.item.id)
          this.updated = true
          this.loading = false
          this.prompt.success = 'Success! Organisation has been removed.'

          // Enable edit
          setTimeout(() => {
            this.$router.push(this.parent)
          }, 3000)
        })
      }
    },
  },
}
</script>
